import {
  FilePlusIcon,
  FileSearchIcon,
  ScrollTextIcon,
  TicketIcon,
  UserPlusIcon,
  UsersIcon,
} from 'lucide-react';

/**
 * @typedef {Object} TutorialNote
 * @property {'info' | 'warning'} type - Controls the colour of the callout.
 * @property {string} text - The note itself.
 */

/**
 * @typedef {Object} Tutorial
 * @property {string} id - Value used by the accordion item.
 * @property {import('lucide-react').LucideIcon} icon
 * @property {string} title
 * @property {string} description - One line summary, also used by the search.
 * @property {string[]} tags - Keywords shown as badges and used by the search.
 * @property {string[]} steps - Ordered instructions.
 * @property {TutorialNote[]} [notes] - Callouts shown after the steps.
 * @property {{ to: string, label: string }} [link] - Shortcut to the relevant page.
 */

/**
 * The end to end flow an operator follows, shown as a summary above the tutorials.
 *
 * @type {{ title: string, description: string }[]}
 */
export const OPERATOR_FLOW = [
  {
    title: 'Registar o Cliente',
    description:
      'Se o cliente ainda não existe na plataforma, cria-se um registo com os dados dele.',
  },
  {
    title: 'Recolher o Consentimento',
    description:
      'O cliente aceita as Condições de Participação no tablet de consentimentos.',
  },
  {
    title: 'Inserir a Fatura',
    description:
      'Regista-se a fatura da compra em nome do cliente, na loja onde foi emitida.',
  },
  {
    title: 'Gerar os Cupões',
    description:
      'Depois de a fatura ser aprovada, os cupões do cliente são gerados automaticamente.',
  },
];

/**
 * Step by step tutorials for the day to day tasks of an operator.
 *
 * @type {Tutorial[]}
 */
export const TUTORIALS = [
  {
    id: 'criar-cliente',
    icon: UserPlusIcon,
    title: 'Criar um Cliente novo',
    description:
      'Registar um cliente que ainda não existe na plataforma e recolher o consentimento.',
    tags: ['Clientes', 'Consentimento'],
    steps: [
      'No menu lateral, abra Clientes e carregue em Criar Cliente. No Dashboard também existe um atalho com o mesmo nome.',
      'Preencha os campos obrigatórios: Nome, Apelido, Nº Identificação, Data de Nascimento, Telemóvel e Localidade. O Email é opcional, mas sem email o cliente não consegue aceder ao Portal de Cliente.',
      'A Data de Nascimento tem de seguir o formato dd-MM-yyyy. Pode escrever a data ou escolhê-la no calendário.',
      'Carregue em Criar. A plataforma verifica se já existe algum cliente com o mesmo Nº de Identificação ou com o mesmo Email.',
      'O registo fica Pendente de Consentimento — ainda não é um cliente ativo e ainda não é possível inserir faturas em nome dele.',
      'Peça ao cliente para se dirigir ao tablet de consentimentos, pesquisar o seu Nº de Identificação (ou email) e aceitar as Condições de Participação.',
      'Assim que o cliente aceitar no tablet, a conta passa a existir e já pode inserir faturas.',
    ],
    notes: [
      {
        type: 'warning',
        text: 'Sem a aceitação das Condições de Participação o cliente não pode participar na campanha nem receber cupões.',
      },
      {
        type: 'info',
        text: 'Para ver quem está à espera de consentimento, abra Clientes e carregue em Ver Consentimentos Pendentes.',
      },
    ],
    link: { to: '/backoffice/customers/create', label: 'Criar Cliente' },
  },
  {
    id: 'inserir-fatura',
    icon: FilePlusIcon,
    title: 'Inserir uma Fatura',
    description:
      'Registar a fatura de uma compra em nome de um cliente já existente.',
    tags: ['Faturas', 'Cupões'],
    steps: [
      'No menu lateral, abra Faturas e carregue em Inserir Fatura. No Dashboard também existe um atalho com o mesmo nome.',
      'O campo Campanha é preenchido automaticamente com a campanha que está a decorrer.',
      'Escreva o Nº Identificação do cliente. A pesquisa é automática — aguarde uns instantes e os dados do cliente aparecem preenchidos.',
      'Se aparecer a mensagem de que não existe nenhum cliente com aquele número, carregue em Carregue aqui para criar o Cliente e siga o tutorial de criação de cliente.',
      'Confirme que a caixa Condições de Participação está assinalada. Se não estiver, carregue em Criar Pedido de Atualização de Consentimento do Cliente, peça ao cliente para aceitar no tablet e depois carregue em Verificar Consentimento do Cliente.',
      'Preencha o Nº da Fatura, o Valor da Fatura, a Data de Emissão (dd-MM-yyyy) e a Loja onde a fatura foi emitida.',
      'Por baixo do valor a plataforma mostra a quantos cupões aquela fatura corresponde. Confirme este número com o cliente.',
      'Se o cliente trouxer mais do que uma fatura, carregue em Adicionar outra Fatura e repita o passo anterior para cada uma.',
      'Carregue em Inserir, confira o resumo que aparece na janela de confirmação e carregue em Continuar.',
    ],
    notes: [
      {
        type: 'warning',
        text: 'A inserção de faturas é irreversível. Confirme o número, o valor e a loja antes de confirmar.',
      },
      {
        type: 'info',
        text: 'A Data de Emissão tem de estar entre a data de início e a data de fecho da campanha, e o valor não pode ser inferior ao valor mínimo definido para a campanha.',
      },
      {
        type: 'warning',
        text: 'Se aparecer o aviso vermelho de Funcionários Internos / Lojas, confirme os dados do participante com o supervisor antes de avançar.',
      },
    ],
    link: { to: '/backoffice/invoices/create', label: 'Inserir Fatura' },
  },
  {
    id: 'aprovar-fatura',
    icon: FileSearchIcon,
    title: 'Aprovar ou Rejeitar uma Fatura pendente',
    description:
      'Validar as faturas que ficaram a aguardar aprovação e libertar os cupões.',
    tags: ['Faturas', 'Aprovação'],
    steps: [
      'No Dashboard, a lista Faturas Pendentes mostra as faturas desta campanha que estão à espera de aprovação, da mais antiga para a mais recente.',
      'Carregue no ícone à direita da fatura para a abrir. Em alternativa, abra Faturas no menu lateral e use o filtro Estado para escolher Pendente.',
      'Carregue em Comprovativo para abrir a imagem ou o PDF que o cliente submeteu, quando existe.',
      'Compare o comprovativo com os dados do formulário: Nº da fatura, Valor, Loja e Data de Emissão. Corrija os campos se algum estiver errado.',
      'Se os dados estiverem corretos, carregue em Aprovar, assinale a caixa de confirmação e confirme. Os cupões do cliente são gerados nesse momento.',
      'Se a fatura não puder ser aceite, carregue em Rejeitar, escolha o Motivo da lista e confirme. Se escolher Outro, escreva o motivo.',
    ],
    notes: [
      {
        type: 'warning',
        text: 'Aprovar e rejeitar são ações irreversíveis. Uma fatura aprovada já não pode ser editada.',
      },
      {
        type: 'info',
        text: 'O motivo da rejeição fica registado, por isso escolha sempre o que melhor descreve o problema.',
      },
    ],
    link: { to: '/backoffice/invoices', label: 'Ver Faturas' },
  },
  {
    id: 'consultar-cupoes',
    icon: TicketIcon,
    title: 'Consultar os Cupões de um Cliente',
    description:
      'Confirmar quantos cupões um cliente tem e a que fatura correspondem.',
    tags: ['Cupões', 'Clientes'],
    steps: [
      'No menu lateral, abra Cupões.',
      'Use a pesquisa da coluna Cliente para encontrar o cliente, ou a coluna Nº da Fatura para encontrar os cupões de uma fatura em concreto.',
      'A coluna Estado mostra se o cupão está Válido ou Anulado.',
      'Em alternativa, abra a ficha do cliente em Clientes para ver, no mesmo sítio, as faturas e os cupões dele na campanha ativa.',
      'Na ficha do cliente existe também o botão Imprimir todos os Cupões, para entregar os cupões ao cliente.',
    ],
    notes: [
      {
        type: 'info',
        text: 'Os cupões só aparecem depois de a fatura correspondente ter sido aprovada.',
      },
    ],
    link: { to: '/backoffice/coupons', label: 'Ver Cupões' },
  },
  {
    id: 'consultar-cliente',
    icon: UsersIcon,
    title: 'Encontrar um Cliente e ver os dados dele',
    description:
      'Pesquisar um cliente para confirmar dados, consentimentos e participações.',
    tags: ['Clientes'],
    steps: [
      'No menu lateral, abra Clientes.',
      'Pesquise pelo Nº Identificação, pelo nome ou pelo email nos campos de pesquisa das colunas.',
      'Carregue no ícone do olho para abrir a ficha do cliente.',
      'Na ficha consegue ver os dados pessoais, os consentimentos aceites e, para a campanha ativa, as faturas inseridas e os cupões atribuídos.',
      'Para corrigir dados do cliente, carregue no ícone de edição na lista ou no botão Editar dentro da ficha.',
    ],
    link: { to: '/backoffice/customers', label: 'Ver Clientes' },
  },
  {
    id: 'consultar-campanha',
    icon: ScrollTextIcon,
    title: 'Ver as regras da Campanha em curso',
    description:
      'Confirmar datas, valor mínimo por cupão e lojas aderentes da campanha.',
    tags: ['Campanhas'],
    steps: [
      'No menu lateral, abra Campanhas e carregue na campanha que está a decorrer.',
      'Confirme a data de início e a data de fecho — só são aceites faturas emitidas dentro deste intervalo.',
      'Confirme o valor mínimo por cupão: é este valor que determina quantos cupões cada fatura gera.',
      'Consulte a lista de lojas aderentes. Uma fatura de uma loja que não esteja na lista não pode ser inserida.',
    ],
    notes: [
      {
        type: 'info',
        text: 'O Dashboard mostra sempre o nome e o estado da campanha que está a decorrer.',
      },
    ],
    link: { to: '/backoffice/campaigns', label: 'Ver Campanhas' },
  },
];

/**
 * Answers to the errors and blocks operators run into most often.
 *
 * @type {{ id: string, question: string, answer: string }[]}
 */
export const FAQ = [
  {
    id: 'sem-campanha',
    question:
      'O Dashboard diz que não existe nenhuma campanha ativa. O que faço?',
    answer:
      'Enquanto não existir uma campanha a decorrer não é possível inserir faturas nem criar participações. Pode continuar a consultar clientes, faturas e cupões das campanhas anteriores. Se acha que já devia existir uma campanha ativa, fale com o supervisor.',
  },
  {
    id: 'fatura-duplicada',
    question: 'Aparece a mensagem "Fatura duplicada". Porquê?',
    answer:
      'Já existe no sistema uma fatura com aquele número para aquela loja. Confirme o número da fatura no talão e pesquise-a em Faturas para ver quando e para que cliente foi inserida.',
  },
  {
    id: 'sem-consentimento',
    question: 'O cliente não tem as Condições de Participação aceites.',
    answer:
      'No formulário de inserção de fatura, carregue em Criar Pedido de Atualização de Consentimento do Cliente. Depois peça ao cliente para aceitar no tablet de consentimentos e carregue em Verificar Consentimento do Cliente para atualizar o formulário.',
  },
  {
    id: 'valor-inferior',
    question: 'O valor da fatura é recusado por ser inferior ao mínimo.',
    answer:
      'Cada campanha tem um valor mínimo por cupão. Faturas abaixo desse valor não dão direito a cupão e não podem ser inseridas. Confirme o valor mínimo na página da campanha.',
  },
  {
    id: 'data-fora-intervalo',
    question: 'A Data de Emissão não é aceite.',
    answer:
      'A data tem de seguir o formato dd-MM-yyyy e tem de estar entre a data de início e a data de fecho da campanha. Confirme as datas da campanha em Campanhas.',
  },
  {
    id: 'loja-em-falta',
    question: 'A loja da fatura não aparece na lista.',
    answer:
      'Só aparecem as lojas aderentes à campanha em curso. Se a loja devia estar na lista, fale com o supervisor para que seja associada à campanha.',
  },
  {
    id: 'sem-permissoes',
    question: 'Aparece "Não tem permissões para ver esta informação".',
    answer:
      'As permissões de operador desta campanha estão desligadas, por isso não é possível criar clientes nem inserir faturas neste momento. Fale com o supervisor.',
  },
];
